/**
 * NuLivePanel
 * Licht, theembaar "NU LIVE" scherm dat de presentationColors prop van
 * NetworkClubMeeting gebruikt. Productie-klaar, TypeScript strict.
 *
 * Props passen 1:1 op bestaande live-pitch state in NetworkClubMeeting.tsx:
 *   colors      ← presentationColors  (of THEME_DEFAULTS)
 *   entry       ← runningEntry
 *   startedAtMs ← livePitchStartedAtMs
 *   duration    ← livePitchTimerDurationSeconds
 */

import React, { useState, useEffect } from 'react';
import { withAlpha, lighten } from '../../../utils/colorUtils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PresentationColors {
  primary: string;
  secondary: string;
  accent: string;
}

interface MediaItem {
  id: string;
  storageUrl: string;
  type?: 'image' | 'video' | 'youtube';
  youtubeVideoId?: string;
}

interface NuLiveEntry {
  presenterName: string;
  title?: string;
  message?: string;
  searchQuestion?: string;
  recommendation?: { targetName: string; reason: string };
  slotNumber?: number;
  mediaItems?: MediaItem[];
}

interface NuLivePanelProps {
  colors: PresentationColors;
  entry: NuLiveEntry | null;
  startedAtMs: number | null;
  duration: number;
  totalSlots?: number;
  isLive: boolean;
}

// ── Timer hook ────────────────────────────────────────────────────────────────

function useRemainingSeconds(startedAtMs: number | null, duration: number): number {
  const [rem, setRem] = useState<number>(duration);
  useEffect(() => {
    if (!startedAtMs) { setRem(duration); return; }
    const tick = (): void => {
      setRem(Math.max(0, duration - (Date.now() - startedAtMs) / 1000));
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [startedAtMs, duration]);
  return rem;
}

// ── Circular timer ────────────────────────────────────────────────────────────

const CircularTimer: React.FC<{ rem: number; total: number; accent: string }> = ({ rem, total, accent }) => {
  const r = 27;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, rem / total);
  const urgent = pct < 0.25;
  const col = urgent ? '#E74C3C' : accent;

  return (
    <div style={{ position: 'relative', width: 70, height: 70, flexShrink: 0 }}>
      <svg width="70" height="70" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="35" cy="35" r={r} fill="none" stroke={withAlpha(col, 0.15)} strokeWidth="5" />
        <circle cx="35" cy="35" r={r} fill="none" stroke={col} strokeWidth="5"
          strokeDasharray={`${pct * circ} ${circ}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.25s linear, stroke 0.5s' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 17, fontWeight: 800, color: col, lineHeight: 1, transition: 'color 0.5s' }}>
          {Math.ceil(rem)}
        </span>
        <span style={{ fontSize: 9, color: withAlpha(col, 0.6), marginTop: 1 }}>sec</span>
      </div>
    </div>
  );
};

// ── Media carousel ────────────────────────────────────────────────────────────

const Carousel: React.FC<{ items: MediaItem[]; accent: string }> = ({ items, accent }) => {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (items.length <= 1) return;
    const id = window.setInterval(() => {
      setVisible(false);
      setTimeout(() => { setIdx((i) => (i + 1) % items.length); setVisible(true); }, 280);
    }, 4_000);
    return () => window.clearInterval(id);
  }, [items]);

  if (!items.length) return null;
  const current = items[idx];
  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', height: 148, position: 'relative',
                  background: '#F1F5F9' }}>
      <img src={current.storageUrl} alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover',
                 opacity: visible ? 1 : 0, transition: 'opacity 0.28s ease' }} />
      {items.length > 1 && (
        <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
                      display: 'flex', gap: 5 }}>
          {items.map((_, i) => (
            <button key={i} aria-label={`Foto ${i + 1}`} onClick={() => setIdx(i)} style={{
              width: i === idx ? 18 : 6, height: 6, borderRadius: 3,
              background: i === idx ? accent : 'rgba(255,255,255,0.65)',
              border: 'none', padding: 0, cursor: 'pointer', transition: 'all 0.3s',
            }} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── NuLivePanel ───────────────────────────────────────────────────────────────

const NuLivePanel: React.FC<NuLivePanelProps> = ({
  colors,
  entry,
  startedAtMs,
  duration,
  totalSlots = 24,
  isLive,
}) => {
  const rem = useRemainingSeconds(startedAtMs, duration);
  const pct = rem / duration;
  const urgent = pct < 0.25 && isLive;

  return (
    <div style={{
      background: '#fff',
      border: `1.5px solid ${withAlpha(colors.primary, 0.18)}`,
      borderRadius: 16,
      boxShadow: `0 4px 28px ${withAlpha(colors.primary, 0.10)}, 0 1px 4px rgba(0,0,0,0.05)`,
      overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      {/* Top accent bar */}
      <div style={{ height: 4, background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})` }} />

      {!isLive || !entry ? (
        /* ── WAITING STATE ─────────────────────────────── */
        <div style={{ padding: '28px 22px', display: 'flex', flexDirection: 'column',
                      alignItems: 'center', gap: 14, textAlign: 'center' }}>
          <div style={{ position: 'relative', width: 56, height: 56 }}>
            <div style={{ position: 'absolute', inset: 0, borderRadius: '50%',
                          border: `2px solid ${withAlpha(colors.primary, 0.3)}`,
                          animation: 'nu-pulse-ring 2s ease-out infinite' }} />
            <div style={{ position: 'absolute', inset: 10, borderRadius: '50%',
                          background: withAlpha(colors.primary, 0.10),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 18 }}>⏳</div>
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
              Wachten op start
            </div>
            <div style={{ fontSize: 12.5, color: '#6B7280', lineHeight: 1.5 }}>
              Host kiest de volgende spreker
            </div>
          </div>
          <div style={{ background: withAlpha(colors.primary, 0.07),
                        border: `1px solid ${withAlpha(colors.primary, 0.15)}`,
                        borderRadius: 8, padding: '8px 14px',
                        fontSize: 11.5, color: colors.primary }}>
            Klik op "Start" bij een deelnemer
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6,
                        background: '#F9FAFB', borderRadius: 10, padding: '8px 16px' }}>
            <span style={{ fontSize: 11.5, color: '#9CA3AF' }}>Afteller:</span>
            <span style={{ fontSize: 20, fontWeight: 800, color: colors.accent }}>{duration}</span>
            <span style={{ fontSize: 11, color: '#9CA3AF' }}>sec</span>
          </div>
        </div>
      ) : (
        /* ── LIVE STATE ────────────────────────────────── */
        <>
          {/* Header: LIVE pill + slot + timer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '14px 16px 0', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5,
                            background: colors.accent, borderRadius: 20, padding: '3px 10px' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff',
                              animation: 'nu-blink 1.2s ease-in-out infinite' }} />
                <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', letterSpacing: 1 }}>LIVE</span>
              </div>
              {entry.slotNumber !== undefined && (
                <div style={{ background: withAlpha(colors.primary, 0.09),
                              border: `1.5px solid ${withAlpha(colors.primary, 0.2)}`,
                              borderRadius: 20, padding: '2px 10px',
                              fontSize: 11, fontWeight: 700, color: colors.primary }}>
                  #{entry.slotNumber} <span style={{ opacity: 0.45, fontWeight: 400 }}>van {totalSlots}</span>
                </div>
              )}
            </div>
            <CircularTimer rem={rem} total={duration} accent={colors.accent} />
          </div>

          {/* Presenter */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 16px 0' }}>
            <div style={{ width: 50, height: 50, borderRadius: '50%', flexShrink: 0,
                          background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 19, fontWeight: 800, color: '#fff',
                          boxShadow: `0 0 0 3px ${withAlpha(colors.primary, 0.15)}` }}>
              {entry.presenterName?.charAt(0) ?? '?'}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#111827',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {entry.presenterName}
              </div>
              {entry.title && (
                <div style={{ fontSize: 12, color: colors.primary, fontWeight: 600, marginTop: 2 }}>
                  {entry.title}
                </div>
              )}
            </div>
          </div>

          <div style={{ margin: '12px 16px 0', borderTop: `1px solid ${withAlpha(colors.primary, 0.08)}` }} />

          {/* Body */}
          <div style={{ padding: '11px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {entry.message && (
              <div style={{ fontSize: 13, color: '#1F2937', lineHeight: 1.6, padding: '9px 13px',
                            background: lighten(colors.primary, 0.95), borderRadius: 10,
                            borderLeft: `3px solid ${colors.primary}` }}>
                {entry.message}
              </div>
            )}
            {entry.searchQuestion && (
              <div style={{ display: 'flex', gap: 9, alignItems: 'center',
                            background: withAlpha(colors.secondary, 0.06),
                            border: `1.5px solid ${withAlpha(colors.secondary, 0.14)}`,
                            borderRadius: 10, padding: '9px 12px' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                              background: withAlpha(colors.secondary, 0.12),
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 13 }}>🔍</div>
                <div>
                  <div style={{ fontSize: 9.5, fontWeight: 700, color: colors.secondary,
                                textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>
                    Wat zoek ik?
                  </div>
                  <div style={{ fontSize: 12.5, color: '#1F2937', lineHeight: 1.4 }}>
                    {entry.searchQuestion}
                  </div>
                </div>
              </div>
            )}
            {entry.mediaItems && entry.mediaItems.length > 0 && (
              <Carousel items={entry.mediaItems} accent={colors.accent} />
            )}
            {entry.recommendation && (
              <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start',
                            background: withAlpha(colors.accent, 0.07),
                            border: `1.5px solid ${withAlpha(colors.accent, 0.25)}`,
                            borderRadius: 10, padding: '9px 12px' }}>
                <span style={{ fontSize: 15, marginTop: 1 }}>⭐</span>
                <div>
                  <div style={{ fontSize: 9.5, fontWeight: 700, color: colors.accent,
                                textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>
                    Aanbeveling voor {entry.recommendation.targetName}
                  </div>
                  <div style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.5 }}>
                    {entry.recommendation.reason}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Progress bar */}
          <div style={{ height: 5, background: '#F3F4F6' }}>
            <div style={{
              height: '100%', width: `${pct * 100}%`, borderRadius: '0 3px 3px 0',
              background: urgent
                ? 'linear-gradient(90deg,#E74C3C,#C0392B)'
                : `linear-gradient(90deg,${colors.primary},${colors.accent})`,
              transition: 'width 0.25s linear, background 0.5s',
            }} />
          </div>
        </>
      )}

      {/* Keyframes — injected once per mount */}
      <style>{`
        @keyframes nu-blink { 0%,100%{opacity:1} 50%{opacity:0.25} }
        @keyframes nu-pulse-ring { 0%{transform:scale(0.85);opacity:1} 100%{transform:scale(1.7);opacity:0} }
      `}</style>
    </div>
  );
};

export default NuLivePanel;
